import {EmbeddedBlock} from '@tinct/core-api-models';
import {
  Button,
  ColorPicker,
  ColorPickerEyeDropper,
  ColorPickerFormat,
  ColorPickerHue,
  ColorPickerSelection,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Separator,
  Switch,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@tinct/web-components';
import Color from 'color';
import {useCallback, useState} from 'react';

type LogoToggleProps = {
  block: EmbeddedBlock;
  onBlockUpdate?: (block: EmbeddedBlock) => void;
};

type LogoToggleRowProps = {
  label: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
};

const LogoToggleRow = ({label, checked, onCheckedChange}: LogoToggleRowProps) => (
  <div className="flex flex-col p-4">
    <div className="flex items-start gap-3">
      <div className="flex flex-1 flex-col gap-2">
        <div className="flex items-center gap-2.5 pt-[3px]">
          <span className="text-sm font-medium text-foreground">{label}</span>
        </div>
      </div>
      <Switch checked={checked} onCheckedChange={onCheckedChange} />
    </div>
  </div>
);

export const AdaptForDarkBackgroundToggle = ({
  block,
  onBlockUpdate,
}: LogoToggleProps) => {
  const handleChange = useCallback(
    (checked: boolean) => {
      onBlockUpdate?.({ ...block, dark: checked });
    },
    [block, onBlockUpdate]
  );

  return (
    <LogoToggleRow
      label="Adapt for dark background"
      checked={block.dark ?? false}
      onCheckedChange={handleChange}
    />
  );
};

// The old "white logos" rendering — also what white=true blocks were migrated to.
const DEFAULT_LOGO_FILL_COLOR = '#FFFFFF';

type LogoColorMode = 'color' | 'grayscale' | 'custom';

/**
 * Logo rendering mode picker: Color (greyscale=false, no fill), Grayscale
 * (greyscale=true, no fill) and Custom (logoFillColor set, overrides the
 * others). Color/Grayscale expose the dark-background toggle; Custom replaces
 * it with a fill-color field opening a ColorPicker popover, applied on Update.
 */
export const LogoColorModeSection = ({
  block,
  onBlockUpdate,
}: LogoToggleProps) => {
  const fillColor = block.logoFillColor ?? null;
  const mode: LogoColorMode =
    fillColor !== null ? 'custom' : block.greyscale ? 'grayscale' : 'color';

  // Remember the applied custom color so leaving and re-entering the Custom
  // tab restores it instead of resetting to the default.
  const [lastFillColor, setLastFillColor] = useState(
    fillColor ?? DEFAULT_LOGO_FILL_COLOR
  );
  if (fillColor && fillColor !== lastFillColor) {
    setLastFillColor(fillColor);
  }

  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [pendingColor, setPendingColor] = useState(DEFAULT_LOGO_FILL_COLOR);

  const handleModeChange = (value: string) => {
    if (value === 'color') {
      onBlockUpdate?.({ ...block, greyscale: false, logoFillColor: null });
    } else if (value === 'grayscale') {
      onBlockUpdate?.({ ...block, greyscale: true, logoFillColor: null });
    } else {
      onBlockUpdate?.({
        ...block,
        greyscale: false,
        logoFillColor: lastFillColor,
      });
    }
  };

  const handlePickerOpenChange = (open: boolean) => {
    if (open) setPendingColor(fillColor ?? DEFAULT_LOGO_FILL_COLOR);
    setIsPickerOpen(open);
  };

  const handleColorChange = useCallback(
    (rgba: Parameters<typeof Color.rgb>[0]) => {
      setPendingColor(Color.rgb(rgba).hex());
    },
    []
  );

  const handleConfirmColor = () => {
    onBlockUpdate?.({
      ...block,
      greyscale: false,
      logoFillColor: pendingColor.toUpperCase(),
    });
    setIsPickerOpen(false);
  };

  return (
    <div className="flex flex-col overflow-hidden rounded-2xl bg-secondary">
      <div className="p-2">
        <Tabs value={mode} onValueChange={handleModeChange}>
          <TabsList className="w-full">
            <TabsTrigger value="color">Color</TabsTrigger>
            <TabsTrigger value="grayscale">Grayscale</TabsTrigger>
            <TabsTrigger value="custom">Custom</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>
      <Separator />
      {mode === 'custom' ? (
        <div className="flex flex-col gap-2 p-4">
          <span className="text-sm font-medium text-foreground">
            Fill color
          </span>
          <Popover open={isPickerOpen} onOpenChange={handlePickerOpenChange}>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="flex h-9 w-full items-center gap-2 rounded-md border border-input bg-background px-3 text-sm text-foreground shadow-xs transition-colors hover:bg-accent/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label={`Logo fill color ${
                  fillColor ?? DEFAULT_LOGO_FILL_COLOR
                }. Click to modify.`}
              >
                <span
                  aria-hidden="true"
                  className="size-4 shrink-0 rounded-full border border-border"
                  style={{
                    backgroundColor: fillColor ?? DEFAULT_LOGO_FILL_COLOR,
                  }}
                />
                {fillColor ?? DEFAULT_LOGO_FILL_COLOR}
              </button>
            </PopoverTrigger>
            {/* The editor sidebar sits in a fixed z-100 container; the popover
                portals to body so it must stack above it. */}
            <PopoverContent className="z-[110] w-64 p-4" align="start">
              <ColorPicker
                defaultValue={fillColor ?? DEFAULT_LOGO_FILL_COLOR}
                onChange={handleColorChange}
                className="gap-3"
              >
                <ColorPickerSelection className="h-32 rounded-md" />
                <ColorPickerHue />
                <div className="flex items-center gap-2">
                  <ColorPickerEyeDropper />
                  <ColorPickerFormat className="flex-1" />
                </div>
                <div className="flex gap-2 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => setIsPickerOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    className="flex-1"
                    onClick={handleConfirmColor}
                  >
                    Update
                  </Button>
                </div>
              </ColorPicker>
            </PopoverContent>
          </Popover>
        </div>
      ) : (
        <AdaptForDarkBackgroundToggle
          block={block}
          onBlockUpdate={onBlockUpdate}
        />
      )}
    </div>
  );
};

export const LargeLogosToggle = ({
  block,
  onBlockUpdate,
}: LogoToggleProps) => {
  const handleChange = useCallback(
    (checked: boolean) => {
      onBlockUpdate?.({ ...block, largeLogo: checked });
    },
    [block, onBlockUpdate]
  );

  return (
    <LogoToggleRow
      label="Large logos"
      checked={block.largeLogo ?? false}
      onCheckedChange={handleChange}
    />
  );
};
